import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, LogIn, Loader2, ExternalLink } from 'lucide-react';
import {
  fetchAuthConfig,
  isLoggedIn,
  localLogin,
  startFacetLogin,
} from '../services/auth';
import type { AuthConfig } from '../services/auth';
import ThemeToggle from '../components/ui/ThemeToggle';

export default function Login() {
  const [email, setEmail] = useState('admin@atelier.local');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn()) {
      navigate('/');
      return;
    }
    fetchAuthConfig()
      .then(setConfig)
      .catch(() => setConfig({ use_facet_auth: false }))
      .finally(() => setConfigLoading(false));
  }, [navigate]);

  const doLocalLogin = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      await localLogin(email.trim(), password);
      window.location.href = '/';
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = e.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : typeof e.message === 'string'
            ? e.message
            : '登录失败，请检查邮箱和密码'
      );
    } finally {
      setLoading(false);
    }
  };

  const doFacetLogin = async () => {
    if (!config) return;
    setLoading(true);
    setError('');
    try {
      await startFacetLogin(config);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(message || '启动 Facet 登录失败');
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-theme-toggle">
        <ThemeToggle />
      </div>
      <div className="auth-card fade-in">
        <div className="auth-logo">
          <Gem size={30} color="#fff" />
        </div>
        <h1 className="auth-title">练玉坊</h1>
        <p className="auth-subtitle">面向初学者的 Agent 练习工作台</p>

        {configLoading ? (
          <div className="auth-status">
            <Loader2 size={24} className="spin" />
            加载登录配置...
          </div>
        ) : config?.use_facet_auth ? (
          <>
            <div className="auth-form">
              {error && <div className="auth-error">{error}</div>}
              <button className="btn btn-primary btn-block" onClick={doFacetLogin} disabled={loading}>
                {loading ? <Loader2 size={16} className="spin" /> : <ExternalLink size={16} />}
                {loading ? '跳转中...' : '使用 Facet 登录'}
              </button>
            </div>
            <p className="auth-hint">当前已接入 Facet 统一身份中心</p>
          </>
        ) : (
          <>
            <div className="auth-form">
              <div>
                <label className="form-label" htmlFor="login-email">
                  邮箱
                </label>
                <input
                  id="login-email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@atelier.local"
                  onKeyDown={(e) => e.key === 'Enter' && doLocalLogin()}
                />
              </div>

              <div>
                <label className="form-label" htmlFor="login-password">
                  密码
                </label>
                <input
                  id="login-password"
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  onKeyDown={(e) => e.key === 'Enter' && doLocalLogin()}
                />
                <p className="auth-hint" style={{ textAlign: 'left', marginTop: 6 }}>
                  默认管理员密码来自环境变量 ATELIER_ADMIN_PASSWORD
                </p>
              </div>

              {error && <div className="auth-error">{error}</div>}

              <button
                className="btn btn-primary btn-block"
                onClick={doLocalLogin}
                disabled={loading || !email.trim() || !password}
              >
                {loading ? <Loader2 size={16} className="spin" /> : <LogIn size={16} />}
                {loading ? '登录中...' : '进入工作台'}
              </button>
            </div>
            <p className="auth-hint">当前为本地 JWT 认证，token 存储在本地浏览器</p>
          </>
        )}
      </div>
    </div>
  );
}
